import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    BooleanField,
    NumberField,
    SearchInput,
    SelectInput,
    FilterButton,
    CreateButton,
    ExportButton,
    TopToolbar,
    useTranslate,
    FunctionField,
    EditButton,
    ShowButton,
    DeleteButton,
    BulkDeleteButton,
    BulkExportButton,
    ReferenceField,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Chip,
    Typography,
    Tooltip,
    Avatar,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Schedule,
    CheckCircle,
    Cancel,
    Star,
    ElectricBolt,
    Bolt,
    DirectionsCar,
    CalendarToday,
    Warning,
    Add as AddIcon,
    Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FeeSchedule, FEE_ENTITY_TYPE_CHOICES } from '../../types/fee.types';
import { TimezoneAwareDateField } from '../common/TimezoneAwareDateField';
import { useTimezone } from '../../contexts/TimezoneContext';

const DesktopListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

const feeScheduleFilters = [
    <SearchInput key="search" source="search" placeholder="Search schedules..." alwaysOn />,
    <SelectInput
        key="entityType"
        source="entityType"
        choices={FEE_ENTITY_TYPE_CHOICES}
        emptyText="All Entity Types"
        emptyValue=""
    />,
    <SelectInput
        key="isActive"
        source="isActive"
        choices={[
            { id: true, name: 'Active' },
            { id: false, name: 'Inactive' },
        ]}
        emptyText="All"
        emptyValue=""
    />,
];

const EntityTypeField = ({ record }: { record: FeeSchedule }) => {
    const icons = {
        site: <ElectricBolt />,
        load_group: <Bolt />,
        charger: <DirectionsCar />,
    };
    
    const colors = {
        site: 'primary',
        load_group: 'secondary', 
        charger: 'success',
    } as const;
    
    const labels = {
        site: 'Site',
        load_group: 'Load Group',
        charger: 'Charger',
    };
    
    return (
        <Chip
            icon={icons[record.entityType]}
            label={labels[record.entityType]}
            size="small"
            color={colors[record.entityType]}
            variant="outlined"
        />
    );
};

const EntityNameField = ({ record }: { record: FeeSchedule }) => {
    return (
        <Box>
            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
                {record.entity?.name || record.entityId}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                ID: {record.entityId.slice(-8)}
            </Typography>
        </Box>
    );
};

const PriorityField = ({ record }: { record: FeeSchedule }) => {
    const getPriorityLevel = (priority: number) => {
        // Lower numbers have higher priority
        if (priority < 10) return { level: 'H', color: 'error' as const };
        if (priority < 100) return { level: 'M', color: 'warning' as const };
        return { level: 'L', color: 'info' as const };
    };
    
    const { level, color } = getPriorityLevel(record.priority);
    
    return (
        <Tooltip title={`Priority: ${record.priority} (${level === 'H' ? 'High' : level === 'M' ? 'Medium' : 'Low'})`}>
            <Chip
                size="small"
                label={level}
                color={color}
                sx={{ minWidth: 32 }}
            />
        </Tooltip>
    );
};

const TemplateField = ({ record }: { record: FeeSchedule }) => {
    if (record.templateId && record.template) {
        return (
            <Tooltip title={record.template.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                        T
                    </Avatar>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                        {record.template.name}
                    </Typography>
                </Box>
            </Tooltip>
        );
    }
    
    return (
        <Chip
            label="Custom"
            size="small"
            variant="outlined"
            color="default"
        />
    );
};

const EffectivePeriodField = ({ record }: { record: FeeSchedule }) => {
    const { formatDate } = useTimezone();
    const now = new Date();
    const effectiveFrom = new Date(record.effectiveFrom);
    const effectiveUntil = record.effectiveUntil ? new Date(record.effectiveUntil) : null;
    
    const isActive = effectiveFrom <= now && (!effectiveUntil || effectiveUntil >= now);
    const isFuture = effectiveFrom > now;
    const isExpired = effectiveUntil && effectiveUntil < now;
    
    let status = 'Active';
    let color: 'success' | 'warning' | 'error' | 'default' = 'success';
    let icon = <CheckCircle />;
    
    if (isFuture) {
        status = 'Future';
        color = 'warning';
        icon = <CalendarToday />;
    } else if (isExpired) {
        status = 'Expired';
        color = 'error';
        icon = <Cancel />;
    } else if (!record.isActive) {
        status = 'Inactive';
        color = 'default';
        icon = <Cancel />;
    }
    
    const fromDate = formatDate(effectiveFrom, 'date');
    const untilDate = effectiveUntil ? formatDate(effectiveUntil, 'date') : '';
    const dateRange = `${fromDate}${untilDate ? ` - ${untilDate}` : ''}`;
    
    return (
        <Tooltip title={dateRange}>
            <Chip
                icon={icon}
                label={status}
                size="small"
                color={color}
                variant="outlined"
            />
        </Tooltip>
    );
};

const CurrencyField = ({ record }: { record: FeeSchedule }) => {
    const currency = record.currency || record.template?.currency || 'HKD';
    
    return (
        <Typography variant="body2" fontWeight={500}>
            {currency}
        </Typography>
    );
};

const ComponentsPreviewField = ({ record }: { record: FeeSchedule }) => {
    // Prefer the live template's fee_structure when this schedule references a template.
    // Schedule snapshots aren't kept in sync when the template is edited.
    const feeStructure = record.templateId && record.template?.feeStructure
        ? record.template.feeStructure
        : record.feeStructure;
    
    if (!feeStructure) {
        return <Typography variant="caption" color="error">No structure</Typography>;
    }
    
    const components = feeStructure.components || {};
    const enabledComponents = Object.entries(components)
        .filter(([_, component]: [string, any]) => component?.enabled)
        .map(([key, _]) => key);
    
    const componentLabels: Record<string, string> = {
        sessionFee: 'S',
        energyFee: 'E',
        timeFee: 'T',
        idleFee: 'I',
        reservationFee: 'R',
    };
    
    return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {enabledComponents.slice(0, 3).map((component) => (
                <Tooltip key={component} title={component.replace(/([A-Z])/g, ' $1').trim()}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                        {componentLabels[component] || component[0]}
                    </Avatar>
                </Tooltip>
            ))}
            {enabledComponents.length > 3 && (
                <Tooltip title={`+${enabledComponents.length - 3} more`}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>
                        +{enabledComponents.length - 3}
                    </Avatar>
                </Tooltip>
            )}
        </Box>
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const { formatDate } = useTimezone();
    if (isLoading || !data) return null;

    const getEffectiveStatus = (record: FeeSchedule) => {
        const now = new Date();
        const effectiveFrom = new Date(record.effectiveFrom);
        const effectiveUntil = record.effectiveUntil ? new Date(record.effectiveUntil) : null;
        const isFuture = effectiveFrom > now;
        const isExpired = effectiveUntil && effectiveUntil < now;

        if (isFuture) return { label: 'Future', color: 'warning' as const };
        if (isExpired) return { label: 'Expired', color: 'error' as const };
        if (!record.isActive) return { label: 'Inactive', color: 'default' as const };
        return { label: 'Active', color: 'success' as const };
    };

    const entityLabels: Record<string, string> = {
        site: 'Site',
        load_group: 'Load Group',
        charger: 'Charger',
    };

    const entityColors: Record<string, 'primary' | 'secondary' | 'success'> = {
        site: 'primary',
        load_group: 'secondary',
        charger: 'success',
    };

    const getPriorityChip = (priority: number) => {
        if (priority < 10) return { level: 'H', color: 'error' as const };
        if (priority < 100) return { level: 'M', color: 'warning' as const };
        return { level: 'L', color: 'info' as const };
    };

    return (
        <Box>
            {data.map((record: any) => {
                const status = getEffectiveStatus(record);
                const priorityInfo = getPriorityChip(record.priority);
                return (
                    <RecordContextProvider key={record.id} value={record}>
                        <Box
                            onClick={() => navigate(`/fee-schedules/${record.id}/show`)}
                            sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                    {record.name}
                                </Typography>
                                <Chip
                                    label={status.label}
                                    size="small"
                                    color={status.color}
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.65rem', flexShrink: 0 }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label={entityLabels[record.entityType] || record.entityType}
                                    size="small"
                                    color={entityColors[record.entityType] || 'default'}
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.6rem' }}
                                />
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                                    {record.entity?.name || record.entityId?.slice(-8)}
                                </Typography>
                                <Chip
                                    label={priorityInfo.level}
                                    size="small"
                                    color={priorityInfo.color}
                                    sx={{ height: 20, fontSize: '0.6rem', minWidth: 24 }}
                                />
                                {record.templateId && record.template ? (
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                                        {record.template.name}
                                    </Typography>
                                ) : (
                                    <Chip
                                        label="Custom"
                                        size="small"
                                        variant="outlined"
                                        color="default"
                                        sx={{ height: 20, fontSize: '0.6rem' }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </RecordContextProvider>
                );
            })}
        </Box>
    );
};

export const FeeSchedulesList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={feeScheduleFilters}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                sort={{ field: 'priority', order: 'DESC' }}
                perPage={25}
                title={translate('resources.fee-schedules.name')}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                        overflow: 'auto',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                            '& .RaDatagrid-tbody tr:last-child td': {
                                borderBottom: 0,
                            },
                            '& .MuiTableCell-root': {
                                padding: '6px 8px',
                            },
                        }}
                    >
                        <TextField
                            source="name"
                            label={translate('resources.fee-schedules.fields.name')}
                            sortable={true}
                            sx={{
                                '& .MuiTypography-root': {
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        />
                        <FunctionField
                            label={translate('resources.fee-schedules.fields.entityType')}
                            render={(record: FeeSchedule) => <EntityTypeField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            label={translate('resources.fee-schedules.fields.entity')}
                            render={(record: FeeSchedule) => <EntityNameField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            label={translate('resources.fee-schedules.fields.priority')}
                            render={(record: FeeSchedule) => <PriorityField record={record} />}
                            sortable={true}
                        />
                        <FunctionField
                            label={translate('resources.fee-schedules.fields.template')}
                            render={(record: FeeSchedule) => <TemplateField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            label={translate('resources.fee-schedules.fields.currency')}
                            render={(record: FeeSchedule) => <CurrencyField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            label={translate('resources.fee-schedules.fields.components')}
                            render={(record: FeeSchedule) => <ComponentsPreviewField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            label={translate('resources.fee-schedules.fields.effectivePeriod')}
                            render={(record: FeeSchedule) => <EffectivePeriodField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            source="createdAt"
                            label={translate('resources.fee-schedules.fields.createdAt')}
                            sortable={true}
                            render={(record: FeeSchedule) => (
                                <TimezoneAwareDateField
                                    source="createdAt"
                                    showTime={false}
                                />
                            )}
                            sx={{
                                '@media (max-width: 1400px)': {
                                    display: 'none'
                                }
                            }}
                        />
                        <FunctionField
                            label="Actions"
                            render={(record: FeeSchedule) => (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <EditButton label="" record={record} />
                                    <ShowButton label="" record={record} />
                                    <DeleteButton label="" record={record} />
                                </Box>
                            )}
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/fee-schedules/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};